import {
  Button,
  CheckInput,
  Icon,
  Modal,
  OutlinedSelectOptionType,
  Select,
  addToast,
} from '@octano/global-ui';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Container, Row } from 'reactstrap';

import {
  addCohortRange,
  deleteCohortRange,
  downloadCohortRangesTemplate,
  getCohortRanges,
  getCohortSetting,
  getPeriodAndSchool,
  getTcTariffList,
  saveCohortSetting,
  updateCohortRange,
} from '../../../api/requests/tuitionContinuityTariff';
import InfoWithTooltip from '../../../components/curriculum/parts/InfoWithTooltip';
import DisplayError from '../../../components/info/DisplayError';
import Loading from '../../../components/info/Loading';
import { useLoadingState } from '../../../hooks/useLoadingState';
import {
  CohortRange,
  PeriodAndSchoolResponse,
} from '../../../types/tuitionContinuityTrariff';
import BulkTariffUploadModal from './parts/BulkTariffUploadModal';
import CohortRangeModal from './parts/CohortRangeModal';
import TcTariffTableLoader from './parts/TcTariffTableLoader';
import TuitionContinuityTariffTable from './parts/TuitionContinuityTariffTable';

export default function TcTariff() {
  const { t } = useTranslation();
  const prefix = 'tuitionContinuityTariff.tariff';
  const {
    loading,
    setLoading,
    errorLoading,
    setErrorLoading,
  } = useLoadingState();
  const [periodAndOffer, setPeriodAnOffer] = useState<PeriodAndSchoolResponse>({
    periods: [],
    offers: [],
  });

  const [selectedOptions, setSelected] = useState<{
    periodId?: number;
    schoolId?: number;
  }>({});

  const { control: cohortControl, reset: cohortReset } = useForm<{
    cohortEnabled: boolean;
  }>({ defaultValues: { cohortEnabled: false } });
  const cohortEnabled = useWatch({
    control: cohortControl,
    name: 'cohortEnabled',
  });
  const lastSavedCohortRef = useRef<boolean>(false);
  const [cohortReady, setCohortReady] = useState<boolean>(false);
  // Una vez activado + persistido el ingreso por cohorte para (período, escuela),
  // la opción es irreversible: el check queda marcado + deshabilitado.
  const [cohortLocked, setCohortLocked] = useState<boolean>(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState<boolean>(
    false,
  );
  const [uploadModalOpen, setUploadModalOpen] = useState<boolean>(false);
  // Si la escuela no tiene planes de estudio en el período, no hay nada que
  // preciar: se deshabilitan la descarga y la carga de plantilla.
  const [hasStudyPlans, setHasStudyPlans] = useState<boolean>(false);

  const [cohortRanges, setCohortRanges] = useState<CohortRange[]>([]);
  const [cohortRangesReady, setCohortRangesReady] = useState<boolean>(false);
  const [rangeModal, setRangeModal] = useState<{
    open: boolean;
    mode: 'initial' | 'add' | 'edit';
    editingRangeId?: number;
    fromYear?: number;
    toYear?: number;
  }>({ open: false, mode: 'add' });
  const [savingRange, setSavingRange] = useState<boolean>(false);
  // Confirmación de eliminación del rango COMPARTIDO (afecta a todos los planes).
  const [deleteRange, setDeleteRange] = useState<{
    open: boolean;
    range?: CohortRange;
  }>({ open: false });
  const [deletingRange, setDeletingRange] = useState<boolean>(false);

  const loadPeriodAnOffer = useCallback(async () => {
    setLoading(true);
    const { data, error } = await getPeriodAndSchool();
    if (error) {
      setErrorLoading(error.code);
    }
    if (data) {
      setErrorLoading(undefined);
      setPeriodAnOffer(data.data);
    }
    setLoading(false);
  }, [setLoading, setErrorLoading]);

  // Carga los rangos de cohorte COMPARTIDOS del período + escuela.
  const loadCohortRanges = useCallback(
    async (periodId: number, schoolId: number) => {
      const { data } = await getCohortRanges(periodId, schoolId);
      setCohortRanges(data ?? []);
      setCohortRangesReady(true);
    },
    [],
  );

  const options = useMemo(
    () => ({
      periodsOption: periodAndOffer.periods.map<OutlinedSelectOptionType>(
        (p) => ({
          label: p.name,
          value: p.id,
        }),
      ),
      offerOptions: periodAndOffer.offers.map<OutlinedSelectOptionType>(
        (s) => ({ label: s.name, value: s.id }),
      ),
    }),
    [periodAndOffer],
  );

  const handleChangePeriod = useCallback(
    (option: OutlinedSelectOptionType) => {
      setSelected((prevState) => {
        return { ...prevState, periodId: Number(option.value) };
      });
    },
    [setSelected],
  );

  const handleChangeSchool = useCallback(
    (option: OutlinedSelectOptionType) => {
      setSelected((prevState) => {
        return { ...prevState, schoolId: Number(option.value) };
      });
    },
    [setSelected],
  );

  // Descarga la plantilla base (xlsx) para la carga masiva de rangos de cohorte.
  const onDownloadTemplate = useCallback(async () => {
    const { periodId, schoolId } = selectedOptions;
    if (periodId === undefined || schoolId === undefined) {
      return;
    }
    setDownloadingTemplate(true);
    const res = await downloadCohortRangesTemplate(periodId, schoolId, {
      inscriptionLabel: t('tuitionContinuityTariff.cohort.headerInscription'),
      tuitionLabel: t('tuitionContinuityTariff.cohort.headerTuition'),
      creditLabel: t('tuitionContinuityTariff.cohort.headerCredit'),
    });
    if (res.error || !res.data) {
      addToast({
        icon: 'error',
        color: 'danger',
        text: t('common.errors.download'),
      });
    } else {
      const a = document.createElement('a');
      document.body.appendChild(a);
      const url = window.URL.createObjectURL(res.data as Blob);
      a.href = url;
      a.download = 'plantilla-aranceles-continuidad.xlsx';
      a.click();
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        a.remove();
      }, 0);
    }
    setDownloadingTemplate(false);
  }, [selectedOptions, t]);

  // Abre el modal global "+ AGREGAR RANGO" (sobre un set ya iniciado).
  const onOpenAddRange = useCallback(() => {
    setRangeModal({ open: true, mode: 'add' });
  }, []);

  // Abre el modal en modo edición, prellenado con la duración del rango. Al
  // confirmar, modifica el rango COMPARTIDO (afecta a todos los planes/ofertas).
  const onEditRange = useCallback((range: CohortRange) => {
    setRangeModal({
      open: true,
      mode: 'edit',
      editingRangeId: range.id,
      fromYear: range.fromYear,
      toYear: range.toYear,
    });
  }, []);

  // Abre la confirmación para eliminar un rango COMPARTIDO. Al confirmar, borra
  // el rango y TODAS sus celdas de montos en cada sede/jornada de cada plan.
  const onDeleteRange = useCallback((range: CohortRange) => {
    setDeleteRange({ open: true, range });
  }, []);

  // Cierra la confirmación de eliminación del rango.
  const onCloseDeleteRange = useCallback(() => {
    setDeleteRange({ open: false });
  }, []);

  // Confirma la eliminación: borra el rango compartido y recarga el listado para
  // que desaparezca de todos los planes junto con sus montos configurados.
  const onConfirmDeleteRange = useCallback(async () => {
    const { periodId, schoolId } = selectedOptions;
    const range = deleteRange.range;
    if (
      range === undefined ||
      periodId === undefined ||
      schoolId === undefined
    ) {
      return;
    }
    setDeletingRange(true);
    const { error } = await deleteCohortRange(range.id);
    if (error) {
      addToast({
        icon: 'error',
        color: 'danger',
        text: t('common.errors.delete'),
      });
      setDeletingRange(false);
      return;
    }
    await loadCohortRanges(periodId, schoolId);
    addToast({
      icon: 'success',
      color: 'success',
      text: t('tuitionContinuityTariff.cohort.deleteRangeSuccess'),
    });
    setDeleteRange({ open: false });
    setDeletingRange(false);
  }, [selectedOptions, deleteRange.range, loadCohortRanges, t]);

  // Cierra el modal. Si era el rango inicial y se cancela, revierte el check.
  const onCloseRangeModal = useCallback(() => {
    if (rangeModal.mode === 'initial') {
      lastSavedCohortRef.current = false;
      cohortReset({ cohortEnabled: false });
    }
    setRangeModal({ open: false, mode: 'add' });
  }, [rangeModal.mode, cohortReset]);

  // Confirma el rango: lo agrega y refresca. En el flujo inicial, además
  // persiste el flag habilitado (solo tras añadir el primer rango).
  const onConfirmRange = useCallback(
    async (fromYear: number, toYear: number) => {
      const { periodId, schoolId } = selectedOptions;
      if (periodId === undefined || schoolId === undefined) {
        return;
      }
      setSavingRange(true);
      // Modo edición: modifica la duración del rango compartido por id.
      if (
        rangeModal.mode === 'edit' &&
        rangeModal.editingRangeId !== undefined
      ) {
        const { error } = await updateCohortRange(
          rangeModal.editingRangeId,
          fromYear,
          toYear,
        );
        if (error) {
          const message =
            (error.data as { message?: string })?.message ||
            t('tuitionContinuityTariff.cohort.rangeOverlapError') ||
            '';
          addToast({ icon: 'error', color: 'danger', text: message });
          setSavingRange(false);
          return;
        }
        // Recarga los rangos compartidos para refrescar la etiqueta de Cohorte
        // en todos los planes; las celdas mantienen su id (mismo rango).
        await loadCohortRanges(periodId, schoolId);
        setRangeModal({ open: false, mode: 'add' });
        setSavingRange(false);
        return;
      }
      const { error } = await addCohortRange(
        periodId,
        schoolId,
        fromYear,
        toYear,
      );
      if (error) {
        const message =
          (error.data as { message?: string })?.message ||
          t('tuitionContinuityTariff.cohort.rangeOverlapError') ||
          '';
        addToast({ icon: 'error', color: 'danger', text: message });
        setSavingRange(false);
        return;
      }
      if (rangeModal.mode === 'initial') {
        lastSavedCohortRef.current = true;
        setCohortLocked(true);
        await saveCohortSetting(periodId, schoolId, true);
      }
      await loadCohortRanges(periodId, schoolId);
      setRangeModal({ open: false, mode: 'add' });
      setSavingRange(false);
    },
    [
      selectedOptions,
      rangeModal.mode,
      rangeModal.editingRangeId,
      loadCohortRanges,
      t,
    ],
  );

  const texts = useMemo(
    () => ({
      select: {
        period: t(`${prefix}.form.selectPeriod`),
        school: t(`${prefix}.form.selectSchool`),
      },
      header: {
        title: t(`${prefix}.title`),
        subtitle: t(`${prefix}.subTitle`),
      },
      error: {
        data: t(`${prefix}.error.data`),
      },
      cohort: {
        enableLabel: t('tuitionContinuityTariff.cohort.enableLabel'),
        enableTooltip: 'tuitionContinuityTariff.cohort.enableTooltip',
        individualConfigTitle: t(
          'tuitionContinuityTariff.cohort.individualConfigTitle',
        ),
        individualConfigDescription: t(
          'tuitionContinuityTariff.cohort.individualConfigDescription',
        ),
        massiveConfigTitle: t(
          'tuitionContinuityTariff.cohort.massiveConfigTitle',
        ),
        massiveConfigDescription: t(
          'tuitionContinuityTariff.cohort.massiveConfigDescription',
        ),
        downloadTemplate: t('tuitionContinuityTariff.cohort.downloadTemplate'),
        uploadTemplate: t('tuitionContinuityTariff.cohort.uploadTemplate'),
        noStudyPlans: t('tuitionContinuityTariff.cohort.noStudyPlans'),
        initialRangeTitle: t(
          'tuitionContinuityTariff.cohort.initialRangeTitle',
        ),
        initialRangeSubtitle: t(
          'tuitionContinuityTariff.cohort.initialRangeSubtitle',
        ),
        initialRangeWarning: t(
          'tuitionContinuityTariff.cohort.initialRangeWarning',
        ),
        addRangeTitle: t('tuitionContinuityTariff.cohort.addRangeTitle'),
        editRangeTitle: t('tuitionContinuityTariff.cohort.editRangeTitle'),
        addRange: t('tuitionContinuityTariff.cohort.addRange'),
        deleteRangeTitle: t('tuitionContinuityTariff.cohort.deleteRangeTitle'),
        deleteRangeWarning: t(
          'tuitionContinuityTariff.cohort.deleteRangeWarning',
        ),
      },
    }),
    [t],
  );

  useEffect(() => {
    loadPeriodAnOffer();
  }, [loadPeriodAnOffer]);

  // Carga el flag + los rangos de cohorte cada vez que cambia período/escuela.
  useEffect(() => {
    const { periodId, schoolId } = selectedOptions;
    if (periodId === undefined || schoolId === undefined) {
      setCohortReady(false);
      setCohortRangesReady(false);
      setCohortRanges([]);
      setCohortLocked(false);
      setHasStudyPlans(false);
      lastSavedCohortRef.current = false;
      cohortReset({ cohortEnabled: false });
      return;
    }
    let active = true;
    setCohortReady(false);
    setCohortRangesReady(false);
    setHasStudyPlans(false);
    (async () => {
      const [
        { data },
        { data: rangesData },
        { data: tariffList },
      ] = await Promise.all([
        getCohortSetting(periodId, schoolId),
        getCohortRanges(periodId, schoolId),
        getTcTariffList(periodId, schoolId),
      ]);
      if (!active) {
        return;
      }
      setHasStudyPlans((tariffList?.data?.length ?? 0) > 0);
      const enabled = data?.enabled ?? false;
      lastSavedCohortRef.current = enabled;
      // Si ya estaba activado al cargar el período, la opción queda bloqueada.
      setCohortLocked(enabled);
      cohortReset({ cohortEnabled: enabled });
      setCohortRanges(rangesData ?? []);
      setCohortRangesReady(true);
      setCohortReady(true);
    })();
    return () => {
      active = false;
    };
  }, [selectedOptions, cohortReset]);

  // Persiste el flag cuando el usuario lo cambia. Al activar sin rangos, pide
  // primero el rango inicial (no persiste hasta confirmarlo).
  useEffect(() => {
    const { periodId, schoolId } = selectedOptions;
    if (
      !cohortReady ||
      !cohortRangesReady ||
      periodId === undefined ||
      schoolId === undefined
    ) {
      return;
    }
    if (cohortEnabled === lastSavedCohortRef.current) {
      return;
    }
    if (cohortEnabled && cohortRanges.length === 0) {
      setRangeModal({ open: true, mode: 'initial' });
      return;
    }
    lastSavedCohortRef.current = cohortEnabled;
    (async () => {
      const { error } = await saveCohortSetting(
        periodId,
        schoolId,
        cohortEnabled,
      );
      if (error) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t('common.errors.save'),
        });
      }
    })();
  }, [
    cohortEnabled,
    cohortReady,
    cohortRangesReady,
    cohortRanges.length,
    selectedOptions,
    t,
  ]);
  if (errorLoading) {
    return (
      <DisplayError
        insideCard
        textBody={texts.error.data}
        retryAction={() => loadPeriodAnOffer()}
        loadingAction={loading}
      />
    );
  }
  if (loading) {
    return <Loading insideCard />;
  }

  return (
    <Container fluid className="g-table-container pt-4">
      <p className="fs-20 text-uppercase text-primary fw-700 ">
        {texts.header.title}
      </p>
      <p> {texts.header.subtitle}</p>
      <Row>
        <Col md={6}>
          <Select
            name="selection-period"
            label={texts.select.period}
            isClearable={false}
            options={options.periodsOption}
            onChange={handleChangePeriod}
          />
        </Col>
        <Col md={6}>
          <Select
            name="selection-school"
            label={texts.select.school}
            isClearable={false}
            options={options.offerOptions}
            onChange={handleChangeSchool}
          />
        </Col>
      </Row>
      {/* Mientras cargan setting/rangos/planes del período+escuela se muestra
          UN solo loading: sin esto los botones de plantilla y el check
          aparecían a destiempo respecto de la tabla (pop-in). */}
      {selectedOptions.schoolId && selectedOptions.periodId && !cohortReady && (
        <Loading insideCard className="mt-4" />
      )}
      {selectedOptions.schoolId && selectedOptions.periodId && cohortReady && (
        <>
          <hr className="my-4" />
          <Row className="mb-4">
            <Col md={6} className="mb-4 mb-md-0">
              <p className="fs-18 text-uppercase text-primary fw-700">
                {texts.cohort.individualConfigTitle}
              </p>
              <p>{texts.cohort.individualConfigDescription}</p>
            </Col>
            {/* La carga masiva (plantilla) siempre está disponible para el
                período + escuela seleccionados. */}
            <Col md={6}>
              <p className="fs-18 text-uppercase text-primary fw-700">
                {texts.cohort.massiveConfigTitle}
              </p>
              <p>{texts.cohort.massiveConfigDescription}</p>
              <div></div>

              <Button
                fullwidth
                type="button"
                text={texts.cohort.downloadTemplate}
                size="sm"
                outlined
                icon="download"
                loading={downloadingTemplate}
                disabled={!hasStudyPlans}
                onClick={onDownloadTemplate}
              />
              <Button
                fullwidth
                type="button"
                text={texts.cohort.uploadTemplate}
                size="sm"
                icon="upload"
                disabled={!hasStudyPlans}
                onClick={() => setUploadModalOpen(true)}
              />
              {!hasStudyPlans && (
                <p className="fs-14 text-gray mt-2 mb-0">
                  {texts.cohort.noStudyPlans}
                </p>
              )}
            </Col>
          </Row>
        </>
      )}
      <Row className="mb-3">
        {selectedOptions.schoolId && selectedOptions.periodId && cohortReady && (
          <Col xs={3}>
            <div className="d-flex" style={{ gap: '0.5rem' }}>
              {/* Sin planes de estudio no hay nada que preciar: se deshabilita
                  igual que la descarga/carga de plantillas. */}
              <CheckInput
                name="cohortEnabled"
                label={texts.cohort.enableLabel}
                control={cohortControl}
                disabled={cohortLocked || !hasStudyPlans}
              />
              <InfoWithTooltip
                show
                tooltipId="cohort-enable"
                tooltipText={texts.cohort.enableTooltip}
              />
            </div>
            {/* "AGREGAR RANGO" debe estar disponible siempre que el cohorte
                esté activo, incluso con 0 rangos: si se eliminan todos los
                rangos el setting sigue habilitado (bloqueado) y este botón es
                el ÚNICO camino para volver a crear uno; gatearlo con
                `cohortRanges.length > 0` dejaba un dead-end. */}
            {selectedOptions.schoolId &&
              selectedOptions.periodId &&
              cohortReady &&
              cohortEnabled && (
                <Button
                  fullwidth
                  type="button"
                  text={texts.cohort.addRange}
                  size="sm"
                  icon="plus"
                  onClick={onOpenAddRange}
                />
              )}
          </Col>
        )}
      </Row>

      {selectedOptions.schoolId && selectedOptions.periodId && cohortReady && (
        <TcTariffTableLoader data={selectedOptions}>
          <TuitionContinuityTariffTable
            periodId={selectedOptions.periodId}
            cohortEnabled={cohortEnabled}
            cohortRanges={cohortRanges}
            onEditRange={onEditRange}
            onDeleteRange={onDeleteRange}
          />
        </TcTariffTableLoader>
      )}
      <Modal
        isOpen={deleteRange.open}
        size="md"
        toggle={onCloseDeleteRange}
        unmountOnClose
      >
        <div className="text-center">
          <Icon name="warning" size="52px" />
          <p className="mt-5 mb-2 fs-22 text-dark">
            {texts.cohort.deleteRangeTitle}
          </p>
          <p className="fs-18 mb-5">{texts.cohort.deleteRangeWarning}</p>
          <div
            className="d-flex justify-content-between mt-4"
            style={{ gap: '1.25rem' }}
          >
            <Button
              type="button"
              text={t('common.actions.cancel')}
              className="mt-0"
              outlined
              fullwidth
              onClick={onCloseDeleteRange}
            />
            <Button
              type="button"
              text={t('common.actions.delete')}
              className="mt-0"
              fullwidth
              loading={deletingRange}
              onClick={onConfirmDeleteRange}
            />
          </div>
        </div>
      </Modal>
      <CohortRangeModal
        isOpen={rangeModal.open}
        title={(() => {
          if (rangeModal.mode === 'initial') {
            return texts.cohort.initialRangeTitle;
          }
          if (rangeModal.mode === 'edit') {
            return texts.cohort.editRangeTitle;
          }
          return texts.cohort.addRangeTitle;
        })()}
        subtitle={
          rangeModal.mode === 'initial'
            ? texts.cohort.initialRangeSubtitle
            : undefined
        }
        warning={
          rangeModal.mode === 'initial'
            ? texts.cohort.initialRangeWarning
            : undefined
        }
        defaultFromYear={rangeModal.fromYear}
        defaultToYear={rangeModal.toYear}
        loading={savingRange}
        onClose={onCloseRangeModal}
        onConfirm={onConfirmRange}
      />
      {selectedOptions.periodId && selectedOptions.schoolId && (
        <BulkTariffUploadModal
          isOpen={uploadModalOpen}
          periodId={selectedOptions.periodId}
          schoolId={selectedOptions.schoolId}
          onClose={() => setUploadModalOpen(false)}
          onUploaded={() => {
            // Refresca todo lo del período + escuela tras la carga masiva: una
            // nueva referencia de selección redispara la carga de setting +
            // rangos de cohorte y el refetch de la tabla de precios (ofertas y
            // celdas) servida por el FetchProvider de TcTariffTableLoader.
            setSelected((prev) => ({ ...prev }));
          }}
        />
      )}
    </Container>
  );
}
